import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { homepageApi } from '../services/homepageApi';
import type { Job } from '../data/homepageData';
import { getCompanyFromUrl } from '../utils/company';
import './JobSearchBar.css';

const JobSearchBar: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isExpanded, setIsExpanded] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Get the current company context
  const currentCompany = getCompanyFromUrl();

  // Fetch all jobs on component mount
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        const jobs = await homepageApi.getJobs();
        setAllJobs(jobs);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, [currentCompany]);

  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();

    // Calculate relevance score for each job
    const scoredResults = allJobs
      .map(job => {
        const title = job.title?.toLowerCase() || '';
        const description = job.metaDescription?.toLowerCase() || '';
        const experienceLevel = job.experienceLevel?.toLowerCase() || '';
        const location = job.location?.toLowerCase() || '';
        const skills = (job.skills || []).map(s => s.toLowerCase()).join(' ');

        let score = 0;

        // Title matches are most important
        if (title.startsWith(query)) score += 1000; // Starts with query
        else if (title.includes(` ${query}`)) score += 500; // Word boundary match
        else if (title.includes(query)) score += 300; // Contains query

        // Count character matches in title
        const titleMatches = (title.match(new RegExp(query, 'g')) || []).length;
        score += titleMatches * 100;

        // Skills are second most important
        if (skills.includes(query)) score += 200;

        // Location and experience level
        if (location.includes(query)) score += 50;
        if (experienceLevel.includes(query)) score += 50;

        // Description is least important
        if (description.includes(query)) score += 10;

        return { job, score };
      })
      .filter(item => item.score > 0) // Only include results with matches
      .sort((a, b) => b.score - a.score) // Sort by relevance score (highest first)
      .map(item => item.job);

    return scoredResults.slice(0, 8); // Limit to 8 results
  }, [searchQuery, allJobs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || filteredJobs.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredJobs.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredJobs.length) {
          handleJobSelect(filteredJobs[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
        setIsExpanded(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Navigate to job page
  const handleJobSelect = (job: Job) => {
    navigate(`/job/${job.slug}`);
    setSearchQuery('');
    setIsDropdownOpen(false);
    setSelectedIndex(-1);
    setIsExpanded(false);
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase()
            ? <mark key={index}>{part}</mark>
            : <span key={index}>{part}</span>
        )}
      </>
    );
  };

  return (
    <div className={`job-search-bar ${isExpanded ? 'expanded' : ''}`} ref={searchRef}>
      <div className="search-input-wrapper">
        <svg
          className="search-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder="Stelle suchen..."
          value={searchQuery}
          onChange={(e) => {
            const nextQuery = e.target.value;
            setSearchQuery(nextQuery);
            setSelectedIndex(-1);
            setIsDropdownOpen(nextQuery.trim().length > 0);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsExpanded(true);
            if (searchQuery.trim()) {
              setIsDropdownOpen(true);
            }
          }}
        />
        {searchQuery && (
          <button
            className="clear-button"
            onClick={() => {
              setSearchQuery('');
              setIsDropdownOpen(false);
              setSelectedIndex(-1);
              inputRef.current?.focus();
            }}
            aria-label="Suche löschen"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>

      {isDropdownOpen && filteredJobs.length > 0 && (
        <div className="search-dropdown">
          <div className="dropdown-header">
            {filteredJobs.length} {filteredJobs.length === 1 ? 'Stelle gefunden' : 'Stellen gefunden'}
          </div>
          <ul className="search-results">
            {filteredJobs.map((job, index) => (
              <li
                key={job.id}
                className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => handleJobSelect(job)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="result-title">
                  {highlightMatch(job.title, searchQuery)}
                </div>
                <div className="result-meta">
                  {job.location && (
                    <span className="meta-badge">📍 {job.location}</span>
                  )}
                  {job.experienceLevel && (
                    <span className="meta-badge">{job.experienceLevel}</span>
                  )}
                </div>
                {job.metaDescription && (
                  <div className="result-description">
                    {job.metaDescription.substring(0, 120)}...
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isDropdownOpen && filteredJobs.length === 0 && searchQuery.trim() && !isLoading && (
        <div className="search-dropdown">
          <div className="no-results">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ opacity: 0.3, marginBottom: '0.5rem' }}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <p>Keine Stellen gefunden</p>
            <p className="no-results-hint">Versuchen Sie einen anderen Suchbegriff</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSearchBar;
